import { paths } from '../constants/strings';
import { keys } from './translator/translation_keys';
import { t } from 'i18next';
import { getLocalTime } from './time';

export const actionMap = {
  COMMENT_ADDED: ({ comment, creator, workspaceId }) => {
    let parent = null;
    let url = null;
    switch (comment?.type) {
      case 'Inspection':
        parent =
          comment?.inspection?.subject ?? comment?.inspection?.template?.title;
        url = `/workspace/${workspaceId}/${paths.assessment}/${comment?.parentId}`;
        break;
      case 'Incident':
        parent = comment?.incident?.title ?? comment?.incident?.description;
        url = `${paths.incident}/${comment?.parentId}`;
        break;
      case 'Observation':
        parent =
          comment?.observation?.title ?? comment?.observation?.description;
        url = `${paths.observation}/${comment?.parentId}`;
        break;
      default:
        break;
    }

    return {
      icon: 'comment',
      iconColor: 'primary',
      header: t(keys.notifications.COMMENT_HEADER),
      avatar: creator?.avatarUrl,
      title: t(keys.notifications.COMMENT_TITLE, {
        user: creator?.firstName,
        parent,
      }),
      description: comment?.text,
      url,
      object: comment,
    };
  },
  INSPECTION_ITEM_COMMENT_ADDED: ({
    inspectionItemComment,
    creator,
    workspaceId,
  }) => ({
    icon: 'comment',
    iconColor: 'primary',
    header: t(keys.notifications.COMMENT_HEADER),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.COMMENT_TITLE, {
      user: creator?.firstName,
      parent:
        inspectionItemComment?.inspection?.subject ??
        inspectionItemComment?.inspection?.template?.title,
    }),
    description: inspectionItemComment?.text,
    url: `/workspace/${workspaceId}/${paths.assessment}/${inspectionItemComment?.inspection?.id}`,
    object: inspectionItemComment,
  }),
  ACTION_ITEM_COMMENT: ({ actionItemComment, creator }) => ({
    icon: 'comment',
    iconColor: 'primary',
    header: t(keys.common.ACTION_ITEM),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.ACTION_ITEM_COMMENT_TITLE, {
      user: creator?.firstName,
      actionItemTitle: actionItemComment?.actionItem?.title,
    }),
    description: actionItemComment?.text,
    url: actionItemComment?.actionItem?.isCorrective
      ? `/cpa/${actionItemComment?.actionItem?.cpaId}`
      : `?actionitem=${actionItemComment?.actionItem?.id}`,
    object: actionItemComment,
  }),
  ACTION_ITEM_ASSIGNED: ({ actionItem, creator }) => ({
    icon: 'task_alt',
    iconColor: 'primary',
    header: t(keys.common.ACTION_ITEMS),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.ACTION_ITEM_ASSIGNED_TITLE),
    description: t(keys.notifications.ACTION_ITEM_ASSIGNED_DESCRIPTION, {
      user: `${creator?.firstName} ${creator?.lastName}`,
      title: actionItem?.title,
    }),
    url:
      actionItem?.type === 'INCIDENT_COMMENT'
        ? `/${paths.incident}/${actionItem?.incident?.id}?section=REVIEW`
        : actionItem?.isCorrective
        ? `/cpa/${actionItem?.cpaId}`
        : `?actionitem=${actionItem?.id}`,
    object: actionItem,
    attachedObject: actionItem?.incident,
  }),
  ACTION_ITEM_PARTICIPANT: ({ actionItem, creator }) => ({
    icon: 'task_alt',
    iconColor: 'primary',
    header: t(keys.common.ACTION_ITEMS),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.ACTION_ITEM_PARTICIPANT_TITLE),
    description: t(keys.notifications.ACTION_ITEM_PARTICIPANT_DESCRIPTION, {
      user: `${creator?.firstName} ${creator?.lastName}`,
      title: actionItem?.title,
    }),
    url: actionItem?.isCorrective
      ? `/cpa/${actionItem?.cpaId}`
      : `?actionitem=${actionItem?.id}`,
    object: actionItem,
  }),
  ACTION_ITEM_REMINDER: ({ actionItem, creator }) => ({
    icon: 'task_alt',
    iconColor: 'yellow',
    header: t(keys.common.ACTION_ITEM),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.ACTION_ITEM_REMINDER_TITLE),
    description: t(keys.notifications.ACTION_ITEM_REMINDER_DESCRIPTION, {
      variable:
        t(keys.notifications[actionItem?.lastNotification]) ||
        t(keys.notifications.DUE),
    }),
    url: actionItem?.isCorrective
      ? `/cpa/${actionItem?.cpaId}`
      : `?actionitem=${actionItem?.id}`,
    object: actionItem,
  }),
  ACTION_ITEM_COMPLETE: ({ actionItem, creator }) => ({
    icon: 'task_alt',
    iconColor: 'green',
    header: t(keys.common.ACTION_ITEM),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.ACTION_ITEM_COMPLETE_TITLE),
    description: t(keys.notifications.ACTION_ITEM_COMPLETE_DESCRIPTION, {
      user: `${creator?.firstName} ${creator?.lastName}`,
      title: actionItem?.title,
    }),
    url: actionItem?.isCorrective
      ? `/cpa/${actionItem?.cpaId}`
      : `?actionitem=${actionItem?.id}`,
    object: actionItem,
  }),
  ACTION_ITEM_DUE_DATE: ({ actionItem, creator }) => ({
    icon: 'task_alt',
    iconColor: 'yellow',
    header: t(keys.common.ACTION_ITEM),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.ACTION_ITEM_DUE_DATE_TITLE),
    description: t(keys.notifications.ACTION_ITEM_DUE_DATE_DESCRIPTION, {
      user: `${creator?.firstName} ${creator?.lastName}`,
      title: actionItem?.title,
    }),
    url: actionItem?.isCorrective
      ? `/cpa/${actionItem?.cpaId}`
      : `?actionitem=${actionItem?.id}`,
    object: actionItem,
  }),
  INSPECTION_COMPLETED: ({ inspection, creator, workspaceId }) => ({
    icon: 'inventory',
    iconColor: inspection.passed ? 'green' : 'red',
    header: t(keys.common.ASSESSMENTS),
    avatar: creator?.avatarUrl,
    title: `${inspection?.template?.title} ${
      inspection?.passed ? t(keys.common.PASSED) : t(keys.common.FAILED)
    }`,
    description: t(keys.notifications.ASSESSMENT_DESCRIPTION, {
      user: `${
        !creator ? 'Anonymous' : `${creator?.firstName} ${creator?.lastName}`
      } ${
        inspection?.participants?.length > 1
          ? t(keys.notifications.ASSESSMENT_PARTICIPANTS)
          : ''
      }`,
    }),
    url: `/workspace/${workspaceId}/${paths.assessment}/${inspection?.id}`,
    object: inspection,
  }),
  INCIDENT_REPORTED: ({ creator, incident }) => ({
    icon: 'fmd_bad',
    iconColor: 'red',
    header: t(keys.common.INCIDENT),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.INCIDENT_REPORTED_TITLE),
    description: t(keys.notifications.INCIDENT_REPORTED_DESCRIPTION, {
      user: !creator
        ? 'Anonymous'
        : `${creator?.firstName} ${creator?.lastName}`,
    }),
    url: `/${paths.incident}/${incident?.id}`,
    object: incident,
  }),
  INCIDENT_DEESCALATED: ({ creator, incident }) => ({
    icon: 'fmd_bad',
    iconColor: 'red',
    header: t(keys.common.INCIDENT),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.INCIDENT_DEESCALATED_TITLE),
    description: t(keys.notifications.INCIDENT_DEESCALATED_DESCRIPTION, {
      user: `${creator?.firstName} ${creator?.lastName}`,
    }),
    url: `/${paths.incident}/${incident?.id}`,
    object: incident,
  }),
  INCIDENT_DISMISSED: ({ creator, incident }) => ({
    icon: 'fmd_bad',
    iconColor: 'red',
    header: t(keys.common.INCIDENT),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.INCIDENT_DISMISSED_TITLE),
    description: t(keys.notifications.INCIDENT_DISMISSED_DESCRIPTION, {
      user: `${creator?.firstName} ${creator?.lastName}`,
    }),
    url: `/${paths.incident}/${incident?.id}`,
    object: incident,
  }),
  INCIDENT_APPROVED: ({ creator, incident }) => ({
    icon: 'fmd_bad',
    iconColor: 'green',
    header: t(keys.common.INCIDENT),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.INCIDENT_APPROVED_TITLE),
    description: t(keys.notifications.INCIDENT_APPROVED_DESCRIPTION, {
      user: `${creator?.firstName} ${creator?.lastName}`,
    }),
    url: `/${paths.incident}/${incident?.id}`,
  }),
  INCIDENT_CLOSED: ({ creator, incident }) => ({
    icon: 'fmd_bad',
    iconColor: 'yellow',
    header: t(keys.common.INCIDENT),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.INCIDENT_CLOSED_TITLE),
    description: t(keys.notifications.INCIDENT_CLOSED_DESCRIPTION, {
      user: `${creator?.firstName} ${creator?.lastName}`,
    }),
    url: `/${paths.incident}/${incident?.id}`,
  }),
  EXPENSE_SUBMITTED: ({ creator, expense }) => ({
    icon: 'paid',
    iconColor: 'green',
    header: t(keys.common.EXPENSE),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.EXPENSE_SUBMITTED_TITLE),
    description: t(keys.notifications.EXPENSE_SUBMITTED_DESCRIPTION, {
      variable: `${creator?.firstName} ${creator?.lastName}`,
    }),
    url: `/${paths.expense}?id=${expense?.id}`,
    object: expense,
  }),
  EXPENSE_DENIED: ({ creator, expense }) => ({
    icon: 'paid',
    iconColor: 'red',
    header: t(keys.common.EXPENSE),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.EXPENSE_DENIED_TITLE),
    description: t(keys.notifications.EXPENSE_DENIED_DESCRIPTION),
    url: `/${paths.expense}?id=${expense?.id}`,
    object: expense,
  }),
  TIMECARD_DENIED: ({ creator, timecard }) => ({
    icon: 'timer',
    iconColor: 'red',
    header: t(keys.common.TIMECARD),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.TIMECARD_DENIED_TITLE),
    description: t(keys.notifications.TIMECARD_DENIED_DESCRIPTION),
    url: `/${paths.timecard}?id=${timecard?.id}`,
    object: timecard,
  }),
  FILE_EXPIRY_REMINDER: ({ creator, file, workspaceId }) => ({
    icon: 'folder',
    iconColor: 'yellow',
    header: t(keys.common.FILE),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.FILE_TITLE, { variable: file?.name }),
    description: t(keys.notifications.FILE_DESCRIPTION, {
      name: file?.name,
      expiry:
        t(keys.notifications[file?.lastNotification]) ||
        t(keys.notifications.DUE),
    }),
    url: `/workspace/${workspaceId}/${paths.files}/${
      file?.workspaceId ? 'workspace' : 'global'
    }?file=${file?.id}&parent=${file?.parentId}`,
    object: file,
  }),
  OBSERVATION_CLOSED: ({ creator, observation }) => ({
    icon: 'content_paste_search',
    iconColor: 'yellow',
    header: t(keys.common.OBSERVATION),
    avatar: creator?.avatarUrl ?? null,
    title: t(keys.notifications.OBSERVATION_CLOSED_TITLE),
    description: t(keys.notifications.OBSERVATION_CLOSED_DESCRIPTION),
    url: `/${paths.observation}/${observation?.id}`,
    object: observation,
  }),
  OBSERVATION_SUBMITTED: ({ creator, observation }) => ({
    icon: 'content_paste_search',
    iconColor: 'yellow',
    header: t(keys.common.OBSERVATION),
    avatar: creator?.avatarUrl ?? null,
    title: t(keys.notifications.OBSERVATION_SUBMITTED_TITLE),
    description: t(keys.notifications.OBSERVATION_SUBMITTED_DESCRIPTION),
    url: `/${paths.observation}/${observation?.id}`,
    object: observation,
  }),
  OBSERVATION_REVIEWED: ({ creator, observation }) => ({
    icon: 'content_paste_search',
    iconColor: 'yellow',
    header: t(keys.common.OBSERVATION),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.OBSERVATION_REVIEWED_TITLE),
    description: t(keys.notifications.OBSERVATION_REVIEWED_DESCRIPTION),
    url: `/${paths.observation}/${observation?.id}`,
    object: observation,
  }),
  OBSERVATION_PARTICIPANT: ({ observation, creator }) => ({
    icon: 'content_paste_search',
    iconColor: 'yellow',
    header: t(keys.common.OBSERVATION),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.OBSERVATION_PARTICIPANT_TITLE),
    description: t(keys.notifications.OBSERVATION_PARTICIPANT_DESCRIPTION),
    url: `/${paths.observation}/${observation?.id}`,
    object: observation,
  }),
  ASSESSMENT_REGRET: ({ creator, workspaceId, inspection }) => ({
    icon: 'inventory',
    iconColor: 'yellow',
    header: t(keys.common.ASSESSMENT),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.ASSESSMENT_REGRET_TITLE),
    description: t(keys.notifications.ASSESSMENT_REGRET_DESCRIPTION),
    url: `/workspace/${workspaceId}/${paths.assessment}/${inspection?.id}`,
    object: inspection,
  }),
  ASSET_ASSESSED: ({ inspection, workspaceId, creator }) => ({
    icon: 'local_shipping',
    iconColor: 'yellow',
    header: t(keys.common.ASSETS),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.ASSET_ASSESSED_TITLE, {
      asset: `${inspection?.vehicle?.make} ${inspection?.vehicle?.model} ${inspection?.vehicle?.unitNumber}`,
      user: `${creator?.firstName} ${creator?.lastName}`,
    }),
    description: t(keys.notifications.ASSET_ASSESSED_DESCRIPTION, {
      user: `${creator?.firstName} ${creator?.lastName}`,
      asset: `${inspection?.vehicle?.make} ${inspection?.vehicle?.model} ${inspection?.vehicle?.unitNumber}`,
    }),
    url: `/workspace/${workspaceId}/${paths.assessment}/${inspection?.id}`,
    object: inspection,
  }),
  ASSET_UPDATED: ({ vehicle, creator, workspaceId }) => ({
    icon: 'local_shipping',
    iconColor: 'yellow',
    header: t(keys.common.ASSETS),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.ASSET_UPDATED_TITLE, {
      asset: `${vehicle?.make} ${vehicle?.model} ${vehicle?.unitNumber}`,
      user: `${creator?.firstName} ${creator?.lastName}`,
    }),
    description: t(keys.notifications.ASSET_UPDATED_DESCRIPTION, {
      user: `${creator?.firstName} ${creator?.lastName}`,
      asset: `${vehicle?.make} ${vehicle?.model} ${vehicle?.unitNumber}`,
    }),
    url: `/workspace/${workspaceId}/${paths.asset}/${vehicle?.id}`,
    object: vehicle,
  }),
  TEMPLATE_REVISED: ({ inspectionTemplate, creator, workspaceId }) => ({
    icon: 'inventory',
    iconColor: 'yellow',
    header: t(keys.common.TEMPLATES),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.TEMPLATE_REVISED_TITLE, {
      template: `${inspectionTemplate?.title}`,
      user: `${creator?.firstName} ${creator?.lastName}`,
    }),
    description: t(keys.notifications.TEMPLATE_REVISED_DESCRIPTION, {
      template: `${inspectionTemplate?.title}`,
      user: `${creator?.firstName} ${creator?.lastName}`,
    }),
    url: `/workspace/${workspaceId}/${paths.templates}/${inspectionTemplate?.id}`,
    object: inspectionTemplate,
  }),
  TEMPLATE_USED: ({ inspection, creator, workspaceId }) => ({
    icon: 'inventory',
    iconColor: 'yellow',
    header: t(keys.common.TEMPLATES),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.TEMPLATE_USED_TITLE, {
      template: `${inspection?.template?.title}`,
      user: `${creator?.firstName} ${creator?.lastName}`,
    }),
    description: t(keys.notifications.TEMPLATE_USED_DESCRIPTION, {
      template: `${inspection?.template?.title}`,
      user: `${creator?.firstName} ${creator?.lastName}`,
    }),
    url: `/workspace/${workspaceId}/${paths.assessment}/${inspection?.id}`,
    object: inspection,
  }),
  TEMPLATE_SUBMITTED: ({ inspection, creator, workspaceId }) => ({
    icon: 'inventory',
    iconColor: 'yellow',
    header: t(keys.common.TEMPLATES),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.TEMPLATE_SUBMITTED_TITLE, {
      template: `${inspection?.template?.title}`,
      user: `${creator?.firstName} ${creator?.lastName}`,
    }),
    description: t(keys.notifications.TEMPLATE_SUBMITTED_DESCRIPTION, {
      template: `${inspection?.template?.title}`,
      user: `${creator?.firstName} ${creator?.lastName}`,
    }),
    url: `/workspace/${workspaceId}/${paths.assessment}/${inspection?.id}`,
  }),
  FILE_DELETED: ({ file, creator }) => ({
    icon: 'folder',
    iconColor: 'red',
    header: t(keys.common.FILE),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.FILE_DELETED_TITLE, { variable: file.name }),
    description: t(keys.notifications.FILE_DELETED_DESCRIPTION),
    url: ``,
    object: file,
  }),
  FILE_REVISION: ({ file, creator, workspaceId }) => ({
    icon: 'folder',
    iconColor: 'yellow',
    header: t(keys.common.FILE),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.FILE_REVISION_TITLE, { variable: file?.name }),
    description: t(keys.notifications.FILE_REVISION_DESCRIPTION),
    url: `/workspace/${workspaceId}/${paths.files}/${
      file?.workspaceId ? 'workspace' : 'global'
    }?file=${file?.id}&parent=${file?.parentId}`,
    object: file,
  }),
  ASSESSMENT_CHANGE_REQUEST: ({ inspection, creator, workspaceId }) => ({
    icon: 'inventory',
    iconColor: 'yellow',
    header: t(keys.common.ASSESSMENTS),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.ASSESSMENT_CHANGE_REQUEST_TITLE, {
      title: inspection?.template?.title,
    }),
    description: t(keys.notifications.ASSESSMENT_CHANGE_REQUEST_DESCRIPTION, {
      creator: `${creator?.firstName} ${creator?.lastName}`,
    }),
    url: `/workspace/${workspaceId}/${paths.assessment}/${inspection?.id}`,
    object: inspection,
  }),
  ASSESSMENT_REVIEW: ({ inspection, creator, workspaceId }) => ({
    icon: 'inventory',
    iconColor: 'yellow',
    header: t(keys.common.ASSESSMENTS),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.ASSESSMENT_REVIEW_TITLE, {
      title: inspection?.template?.title,
    }),
    description: t(keys.notifications.ASSESSMENT_REVIEW_DESCRIPTION, {
      creator: creator
        ? `${creator?.firstName} ${creator?.lastName}`
        : 'Someone',
    }),
    url: `/workspace/${workspaceId}/${paths.assessment}/${inspection?.id}`,
    object: inspection,
  }),
  ASSESSMENT_REVIEWED: ({ inspection, creator, workspaceId }) => ({
    icon: 'inventory',
    iconColor: 'green',
    header: t(keys.common.ASSESSMENTS),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.ASSESSMENT_REVIEWED_TITLE),
    description: t(keys.notifications.ASSESSMENT_REVIEWED_DESCRIPTION, {
      title: inspection?.template?.title,
    }),
    url: `/workspace/${workspaceId}/${paths.assessment}/${inspection?.id}`,
    object: inspection,
  }),
  AUDIT_CLOSED: ({ inspection, creator, workspaceId }, { company } = {}) => ({
    icon: 'fact_check',
    iconColor: 'green',
    header: company?.auditName || t(keys.common.AUDIT),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.AUDIT_CLOSED_TITLE, {
      auditName: (company?.auditName || t(keys.common.AUDIT)).replace(
        /s+$/,
        '',
      ),
    }),
    description: t(keys.notifications.AUDIT_CLOSED_DESCRIPTION, {
      user: `${creator?.firstName} ${creator?.lastName}`,
      title: inspection?.template?.title,
    }),
    url: `/workspace/${workspaceId}/${paths.assessment}/${inspection?.id}`,
    object: inspection,
  }),
  INCIDENT_COMMENT_REQUIRED: ({ incident, creator }) => ({
    icon: 'fmd_bad',
    iconColor: 'yellow',
    header: t(keys.common.INCIDENT),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.INCIDENT_COMMENT_TITLE),
    description: t(keys.notifications.INCIDENT_COMMENT_DESCRIPTION),
    url: `/${paths.incident}/${incident?.id}?section=REVIEW`,
    object: incident,
  }),
  EXTENSION_REQUESTED: ({ actionItem, creator }) => ({
    icon: 'task_alt',
    iconColor: 'yellow',
    header: t(keys.common.ACTION_ITEM),
    avatar: creator?.avatarUrl,
    title: 'Extension Requested',
    description: `Extension requested for ${actionItem?.title} on ${actionItem?.correctiveAction?.externalId}`,
    url: `/cpa/${actionItem?.cpaId}`,
    object: actionItem,
  }),
  EXTENSION_GRANTED: ({ actionItem, creator }) => ({
    icon: 'task_alt',
    iconColor: 'yellow',
    header: t(keys.common.ACTION_ITEM),
    avatar: creator?.avatarUrl,
    title: 'Extension Granted',
    description: `Extension granted for ${actionItem?.title} on ${actionItem?.correctiveAction?.externalId}`,
    url: `/cpa/${actionItem?.cpaId}`,
    object: actionItem,
  }),
  EXTENSION_DENIED: ({ actionItem, creator }) => ({
    icon: 'warning',
    iconColor: 'yellow',
    header: t(keys.common.ACTION_ITEM),
    avatar: creator?.avatarUrl,
    title: 'Extension Denied',
    description: `Extension denied for ${actionItem?.title} on ${actionItem?.correctiveAction?.externalId}`,
    url: `/cpa/${actionItem?.cpaId}`,
    object: actionItem,
  }),
  CONDITIONAL_ACTION_TRIGGERED: ({ inspection, creator, workspaceId }) => ({
    icon: 'inventory',
    iconColor: 'yellow',
    header: t(keys.common.ASSESSMENTS),
    avatar: creator?.avatarUrl,
    title: inspection?.template?.title,
    description: 'You have received a message from this assessment',
    url: `/workspace/${workspaceId}/${paths.assessment}/${inspection?.id}`,
    object: inspection,
  }),
  CORRECTIVE_ACTION_CREATED: ({ correctiveAction, creator }) => ({
    icon: 'warning',
    iconColor: 'red',
    header: 'Corrective Action',
    avatar: creator?.avatarUrl,
    title: correctiveAction?.externalId,
    description:
      'You have been added as a participant on a Corrective Preventative Action',
    url: `/${paths.cpa}/${correctiveAction?.id}`,
    object: correctiveAction,
  }),
  CORRECTIVE_ACTION_UPDATED: ({ correctiveAction, creator }) => ({
    icon: 'warning',
    iconColor: 'red',
    header: 'Corrective Action',
    avatar: creator?.avatarUrl,
    title: correctiveAction?.externalId,
    description: 'Corrective Preventative Action has been updated.',
    url: `/${paths.cpa}/${correctiveAction?.id}`,
    object: correctiveAction,
  }),
  INSPECTION_PARTICIPANT: ({ inspection, creator, workspaceId }) => ({
    icon: 'inventory',
    iconColor: 'primary',
    header: t(keys.common.ASSESSMENTS),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.INSPECTION_PARTICIPANT_TITLE),
    description: t(keys.notifications.INSPECTION_PARTICIPANT_DESCRIPTION, {
      user: `${creator?.firstName} ${creator?.lastName}`,
    }),
    url: `/workspace/${workspaceId}/${paths.assessment}/${inspection?.id}`,
    object: inspection,
  }),
  INSPECTION_ITEM_USER_NOTIFY: ({ inspection, creator, workspaceId }) => ({
    icon: 'inventory',
    iconColor: 'primary',
    header: t(keys.common.ASSESSMENTS),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.INSPECTION_ITEM_USER_NOTIFY_TITLE),
    description: t(keys.notifications.INSPECTION_ITEM_USER_NOTIFY_DESCRIPTION, {
      user: `${creator?.firstName} ${creator?.lastName}`,
    }),
    url: `/workspace/${workspaceId}/${paths.assessment}/${inspection?.id}`,
    object: inspection,
  }),
  INCIDENT_PARTICIPANT: ({ incident, creator }) => ({
    icon: 'fmd_bad',
    iconColor: 'primary',
    header: t(keys.common.INCIDENT),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.INCIDENT_PARTICIPANT_TITLE),
    description: t(keys.notifications.INCIDENT_PARTICIPANT_DESCRIPTION, {
      user: `${creator?.firstName} ${creator?.lastName}`,
    }),
    url: `/${paths.incident}/${incident?.id}`,
    object: incident,
  }),
  ADDED_FOR_REVIEW: ({ inspection, creator, workspaceId }) => ({
    icon: 'inventory',
    iconColor: 'primary',
    header: t(keys.common.INSPECTION),
    avatar: creator?.avatarUrl,
    title: t(keys.notifications.ADDED_FOR_REVIEW_TITLE),
    description: t(keys.notifications.ADDED_FOR_REVIEW_DESCRIPTION, {
      user: `${creator?.firstName} ${creator?.lastName}`,
    }),
    url: `/workspace/${workspaceId}/${paths.assessment}/${inspection?.id}`,
    object: inspection,
  }),
  MISSED_CHECK_IN: ({ checkIn, creator, workspaceId }) => ({
    icon: 'accessibility_new',
    iconColor: 'red',
    header: 'Check In',
    avatar: creator?.avatarUrl,
    title: 'Check In Missed',
    description: `${creator?.firstName} ${
      creator?.lastName
    } missed their last check-in at ${getLocalTime(
      checkIn?.responseDeadline,
    ).format('hh:mma')}.`,
    url: `/checkins?id=${checkIn.id}`,
    object: checkIn,
  }),
  USER_RESPONSIBLE_CHANGE: ({ checkIn, creator }) => ({
    icon: 'accessibility_new',
    iconColor: 'primary',
    header: 'Check In',
    avatar: creator?.avatarUrl,
    title: 'User Check In',
    description: `You have been added as the user responsible for ${checkIn?.users[0]?.firstName} ${checkIn?.users[0]?.lastName}.`,
    url: `/checkins?id=${checkIn?.id}`,
    object: checkIn,
  }),
  NEW_REPORT: ({ report, creator }) => ({
    icon: 'insights',
    iconColor: 'green',
    header: 'New Report',
    avatar: creator?.avatarUrl,
    title: 'New Report is Ready',
    description: `New ${report?.title} is ready to be viewed`,
    url:
      report?.type === 'SUMMARY'
        ? `/analytics/?charttab=REPORTS&view=1&pageIndex=0&pageSize=10&sort=dateCreated&desc=true&reportId=${report?.id}`
        : `/report/${report?.id}`,
    object: report,
  }),
};
